import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { Field, FieldProps, Form, Formik } from 'formik'
import React, { Fragment } from 'react'
import * as Yup from 'yup'
import { VasRuleCreate } from '../../../interfaces/VasRule/VasRule'

import { eTypePackage } from '../../../enums/eTypePackage'
import { TypeVasDDL } from '../../../components/TypeVas/TypeVasDDL'
import { FlowVasDDL } from '../../../components/TypeVas/FlowVasDDL'
import { TypeValidationVasDDL } from '../../../components/TypeVas/TypeValidationVasDDL'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { postVasRule } from '../config/api'
import { Filter } from '../../../components/_WMS/FilterComponent'

interface CreateVasRuleModalProps {
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
  requestVasRule: (filters?: Filter[], page?: number, pageSize?: number) => void
}

const initialValues: VasRuleCreate = {
  name: '',
  active: false,
  typeVasId: 0,
  typeItemVasId: 0,
  isMandatory: false,
  typePackageId: eTypePackage.Unidad,
  typeValidationVasId: 0,
  flowVasId: 0
}

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required('Nombre de la regla es requerido'),
  typeVasId: Yup.number().min(1, 'Tipo de VAS es requerido'),
  typeValidationVasId: Yup.number().min(1, 'Modo de validación es requerido'),
  flowVasId: Yup.number().min(1, 'Proceso de aplicación es requerido')
})

export const CreateVasRuleModal: React.FC<CreateVasRuleModalProps> = ({
  handleSnackbar,
  handleCloseModal,
  requestVasRule
}) => {
  const { state: requestVasRuleState, callApi: requestCreateVasRule } = useApi()

  // Se devuelve la promesa para que Formik resetee `isSubmitting` cuando la
  // request termina; si no, el botón queda en 'Procesando...' tras un error.
  const handleSubmit = (values: VasRuleCreate) => {
    return requestCreateVasRule(postVasRule(values))
      .then(() => {
        handleSnackbar('Creación realizada exitosamente', true)
        handleCloseModal()
        requestVasRule()
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
  }

  const typePackageOptions = Object.entries(eTypePackage)
    .filter(([key]) => isNaN(Number(key)))
    .filter(([_, value]) => {
      const numeric = Number(value as any)
      return !Number.isNaN(numeric) && numeric === eTypePackage.Unidad
    })
    .map(([key, value]) => ({
      label: key,
      value: String(value)
    }))

  if (requestVasRuleState.status === Status.Loading) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  return (
    <Fragment>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty, isSubmitting }) => {
          return (
            <Form autoComplete='off'>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography>Nombre de la Regla *</Typography>
                  <Field name='name'>
                    {({ field, meta }: FieldProps<string>) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant='outlined'
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid size={6}>
                  <Typography>Tipo de VAS *</Typography>
                  <Field name='typeVasId'>
                    {({ field, form }: FieldProps<number>) => (
                      <TypeVasDDL
                        value={field.value}
                        onChange={(e: any) =>
                          form.setFieldValue(field.name, e.target.value)
                        }
                        type='outlined'
                        fullWidth
                        required={true}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid size={6}>
                  <Field name='typePackageId'>
                    {({ field, form }: FieldProps<number>) => (
                      <FormControl fullWidth>
                        <Typography>Tipo de Empaque *</Typography>

                        <RadioGroup
                          row
                          value={String(field.value)}
                          onChange={(e) =>
                            form.setFieldValue(
                              field.name,
                              Number(e.target.value)
                            )
                          }
                        >
                          {typePackageOptions.map((opt) => (
                            <FormControlLabel
                              key={opt.value}
                              value={opt.value}
                              control={<Radio />}
                              label={opt.label}
                              disabled
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid size={6}>
                  <Typography>Modo de Validación *</Typography>
                  <Field name='typeValidationVasId'>
                    {({ field, form }: FieldProps<number>) => (
                      <TypeValidationVasDDL
                        value={field.value}
                        onChange={(e: any) =>
                          form.setFieldValue(field.name, e.target.value)
                        }
                        fullWidth
                        required
                        type='outlined'
                      />
                    )}
                  </Field>
                </Grid>
                <Grid size={6}>
                  <Typography>Proceso de Aplicación *</Typography>
                  <Field name='flowVasId'>
                    {({ field, form }: FieldProps<number>) => (
                      <FlowVasDDL
                        value={field.value}
                        onChange={(e: any) =>
                          form.setFieldValue(field.name, e.target.value)
                        }
                        fullWidth
                        required
                        type='outlined'
                      />
                    )}
                  </Field>
                </Grid>
                <Grid size={6}>
                  <Paper
                    variant='outlined'
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 6
                    }}
                  >
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Box>
                        <Typography variant='subtitle1'>Estado</Typography>
                        <Typography variant='body2' color='textSecondary'>
                          Activar o Desactivar esta regla
                        </Typography>
                      </Box>
                      <Field name='active'>
                        {({ field, form }: FieldProps<boolean>) => (
                          <Switch
                            checked={field.value}
                            onChange={(e) =>
                              form.setFieldValue(field.name, e.target.checked)
                            }
                            sx={switchSx}
                          />
                        )}
                      </Field>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={6}>
                  <Paper
                    variant='outlined'
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 6
                    }}
                  >
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Box>
                        <Typography variant='subtitle1'>
                          Obligatorio / Bloqueante
                        </Typography>
                        <Typography variant='body2' color='textSecondary'>
                          Bloquea el proceso si no se completa
                        </Typography>
                      </Box>
                      <Field name='isMandatory'>
                        {({ field, form }: FieldProps<boolean>) => (
                          <Switch
                            checked={field.value}
                            onChange={(e) =>
                              form.setFieldValue(field.name, e.target.checked)
                            }
                            sx={switchSx}
                          />
                        )}
                      </Field>
                    </Box>
                  </Paper>
                </Grid>

                <Grid
                  size={12}
                  display='flex'
                  gap={2}
                  justifyContent='flex-end'
                >
                  <Button
                    variant='outlined'
                    color='primary'
                    onClick={handleCloseModal}
                    disabled={isSubmitting}
                    sx={{ borderRadius: '12px' }}
                  >
                    Cerrar
                  </Button>
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                    disabled={!isValid || !dirty || isSubmitting}
                    sx={{ borderRadius: '12px' }}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={20} color='inherit' />
                      ) : undefined
                    }
                  >
                    {isSubmitting ? 'Procesando...' : 'Guardar'}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Fragment>
  )
}

const switchSx = {
  padding: '8px',
  '& .MuiSwitch-switchBase': {
    padding: '10px',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: 'primary.main',
        opacity: 1
      }
    }
  },
  '& .MuiSwitch-track': {
    borderRadius: 20,
    backgroundColor: '#ccc',
    opacity: 1,
    height: 22,
    width: 42
  },
  '& .MuiSwitch-thumb': {
    width: 18,
    height: 18
  }
}
